import { NavItem, SubNavItem } from './types';

export interface ActiveNav {
  /** Index of the active top-level nav (Payroll, Payments, ...), or -1. */
  activeMainNav: number;
  /** Index of the active sidebar row within the main nav, or -1. */
  activeSubNav: number;
  /** Index of the active tab within the row, in the rendered (isShownInBar) tab list, or -1. */
  activeSubSideNav: number;
}

const NONE: ActiveNav = { activeMainNav: -1, activeSubNav: -1, activeSubSideNav: -1 };

/**
 * Whether a tab is rendered in the sidebar. Single source of truth: `resolveVisibleTabIndex` returns
 * a position within the list this produces, so the renderer must filter with the very same predicate
 * or the resolved index would point at the wrong tab.
 */
export const isTabShown = (tab: SubNavItem) => tab.isShownInBar !== false;

/**
 * Index (within the rendered, isShownInBar-filtered tab list) of the visible tab that owns
 * `pathname` by URL hierarchy: an exact route match, else the longest "leaf" tab whose route is a
 * parent path of it. A tab whose route is itself a prefix of a sibling tab's route (an overview /
 * landing tab) only matches exactly, so it never greedily claims every nested detail page. Returns
 * -1 when no visible tab owns the route, in which case the section header carries the active state.
 */
export const resolveVisibleTabIndex = (tabs: SubNavItem[], pathname: string): number => {
  const visible = tabs.filter(isTabShown);
  const visibleRoutes = visible.map((tab) => tab.href).filter((href): href is string => Boolean(href));
  const isLeaf = (href: string) => !visibleRoutes.some((other) => other !== href && other.startsWith(href + '/'));

  let bestIndex = -1;
  let bestScore = -1;
  visible.forEach((tab, index) => {
    if (!tab.href) return;
    let score = -1;
    if (tab.href === pathname) score = Number.MAX_SAFE_INTEGER;
    else if (isLeaf(tab.href) && pathname.startsWith(tab.href + '/')) score = tab.href.length;
    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });
  return bestScore > -1 ? bestIndex : -1;
};

/**
 * Resolves which sidebar entry should be highlighted for the current route.
 *
 * Two phases:
 *  1. Locate the active section (main nav + row). An exact route match wins; failing that, the
 *     longest route prefix does. Every entry participates - including hidden detail entries - so a
 *     route such as a payroll-run detail still resolves to the section that owns it. This phase
 *     deliberately does not pick a tab; matching a hidden entry only tells us which section owns
 *     the route.
 *  2. Resolve the active tab from the *visible* tabs of that section by URL hierarchy (see
 *     resolveVisibleTabIndex), so a route that matched a hidden detail entry highlights the tab it
 *     belongs to rather than a position outside the rendered list, and -1 leaves the section header
 *     active.
 */
export function resolveActiveNav(
  navigation: NavItem[] | undefined,
  pathname: string,
  asPath: string,
  isAllowed: (rule: NavItem['onlyShownTo']) => boolean,
): ActiveNav {
  if (!navigation) return NONE;

  let mainNav = -1;
  let subNav = -1;
  let subSideNav = -1;

  const matchExact = (href: string | undefined, useAsPath = false) =>
    href !== undefined && href === (useAsPath ? asPath : pathname);
  const matchPrefix = (href: string | undefined) => href !== undefined && pathname.startsWith(href + '/');

  const matched = navigation.some((nav, i) => {
    if (!isAllowed(nav.onlyShownTo)) return false;

    if (!nav.subNavs) {
      if (matchExact(nav.href)) {
        mainNav = i;
        return true;
      }
      return false;
    }

    return nav.subNavs.some((row, j) => {
      // Custom elements (e.g. the sidebar team menu) are widgets, not destinations.
      if (row.isCustomElement) return false;

      if (nav.isTabbedQuery && matchExact(row.href, true)) {
        mainNav = i;
        subNav = j;
        return true;
      }
      if (matchExact(row.href)) {
        mainNav = i;
        subNav = j;
        return true;
      }
      // A tab match (visible or hidden) identifies the owning section; the tab itself is resolved
      // in phase 2.
      return (
        row.subNavs?.some((tab) => {
          if (matchExact(tab.href)) {
            mainNav = i;
            subNav = j;
            return true;
          }
          return false;
        }) ?? false
      );
    });
  });

  // Prefix-match fallback: detail/dynamic pages whose route isn't listed in the nav tree still
  // resolve to the correct section via their parent prefix. Longest match wins so a more specific
  // href beats a shorter sibling.
  if (!matched) {
    let bestMatchLen = 0;
    navigation.forEach((nav, i) => {
      if (!isAllowed(nav.onlyShownTo)) return;
      nav.subNavs?.forEach((row, j) => {
        if (row.isCustomElement) return;
        if (matchPrefix(row.href) && (row.href?.length ?? 0) > bestMatchLen) {
          bestMatchLen = row.href?.length ?? 0;
          mainNav = i;
          subNav = j;
        }
        row.subNavs?.forEach((tab) => {
          if (matchPrefix(tab.href) && (tab.href?.length ?? 0) > bestMatchLen) {
            bestMatchLen = tab.href?.length ?? 0;
            mainNav = i;
            subNav = j;
          }
        });
      });
    });
  }

  // Phase 2: highlight the visible tab that owns the route (or leave the section header active).
  if (mainNav !== -1 && subNav !== -1) {
    const row = navigation[mainNav].subNavs?.[subNav];
    if (row?.subNavs?.length) {
      subSideNav = resolveVisibleTabIndex(row.subNavs, pathname);
    }
  }

  return { activeMainNav: mainNav, activeSubNav: subNav, activeSubSideNav: subSideNav };
}
